import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  ButtonBase,
  ClickAwayListener,
  Divider,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type { PickersRangeCalendarHeaderProps } from "@mui/x-date-pickers-pro";
import dayjs, { Dayjs } from "dayjs";
import { colors } from "../../../../assets/styles/tokens";

// Years/months are laid out in a fixed-column grid (see YEAR_GRID_COLUMNS)
// rather than a single flat list.
const YEAR_GRID_COLUMNS = 4;
const MONTH_INDICES = Array.from({ length: 12 }, (_, i) => i);

// Replaces the default calendar header so month+year is a quick-jump
// grid instead of being locked to whatever month the arrows land on — MUI's
// range calendar only supports a day view (no built-in year-grid switch
// like the single DateCalendar), so jumping months/years is done by hand
// here. The panel is a plain absolutely-positioned Box, not a MUI
// Menu/Select/Popover — those portal to document.body, which sits outside
// whatever Popper/Drawer/ClickAwayListener hosts this calendar, and that
// mismatch is what made a portaled dropdown flash open then instantly close
// (its own opening click read as a click-away). Staying un-portaled avoids
// that entirely, and gives full control over the max-height + scroll.
export const RangeCalendarHeader = ({
  currentMonth,
  onMonthChange,
  minDate,
  maxDate,
  disabled,
}: PickersRangeCalendarHeaderProps<Dayjs>) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  // Year currently being browsed inside the picker panel — independent of
  // `month` (the year-grid step doesn't move the calendar on its own, only
  // the month step does), and reset to the displayed month's year each
  // time the panel is opened.
  const [pickerYear, setPickerYear] = useState<number>(
    dayjs(currentMonth).year(),
  );
  const selectedYearRef = useRef<HTMLButtonElement | null>(null);
  const month = dayjs(currentMonth);
  const monthKey = (d: Dayjs) => d.format("YYYY-MM");
  const isPreviousDisabled =
    !!disabled || monthKey(month) <= monthKey(dayjs(minDate));
  const isNextDisabled =
    !!disabled || monthKey(month) >= monthKey(dayjs(maxDate));

  // Full `minDate`/`maxDate` range — with no bounds set on the picker
  // itself, those resolve to the date-pickers library's own extremes
  // (1900 / 2099), so the grid covers that whole span. Ascending order so
  // it reads top-left (earliest) to bottom-right (latest); the effect
  // below scrolls the browsed/selected year into view on open so the grid
  // doesn't start pinned to 1900.
  const yearOptions = useMemo(() => {
    const minBoundYear = dayjs(minDate).year();
    const maxBoundYear = dayjs(maxDate).year();
    const years: number[] = [];
    for (let y = minBoundYear; y <= maxBoundYear; y++) years.push(y);
    return years;
  }, [minDate, maxDate]);

  useEffect(() => {
    if (pickerOpen) {
      selectedYearRef.current?.scrollIntoView({
        block: "center",
        inline: "nearest",
      });
    }
    // Only re-center when the panel transitions open — not on every
    // pickerYear change, which would fight the user's own scrolling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen]);

  const goToMonth = (next: Dayjs, direction: "left" | "right") => {
    if (!disabled) onMonthChange(next, direction);
  };

  const handleToggleOpen = () => {
    if (disabled) return;
    setPickerOpen((prev) => {
      const next = !prev;
      if (next) setPickerYear(month.year());
      return next;
    });
  };

  const handleSelectMonth = (candidate: Dayjs) => {
    goToMonth(candidate, "left");
    setPickerOpen(false);
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      sx={{ px: 1.5, pt: 1, pb: 0.5, position: "relative" }}
    >
      <Box
        onClick={handleToggleOpen}
        display="flex"
        alignItems="center"
        gap={0.25}
        sx={{ cursor: disabled ? "default" : "pointer" }}
      >
        <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
          {month.format("MMMM YYYY")}
        </Typography>
        <ArrowDropDownIcon
          fontSize="small"
          sx={{ color: disabled ? "text.disabled" : "text.secondary" }}
        />
      </Box>
      {pickerOpen && (
        <ClickAwayListener onClickAway={() => setPickerOpen(false)}>
          <Paper
            elevation={4}
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              mt: 0.5,
              width: 264,
              p: 1.25,
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${YEAR_GRID_COLUMNS}, 1fr)`,
                gap: 0.75,
                // Bounded so the whole panel (year grid + divider + month
                // grid) stays inside the ~300px MUI reserves for this slot
                // in the static range calendar (its root has a fixed
                // height with overflow: hidden) — see the month grid below
                // for the other half of that budget.
                maxHeight: 160,
                overflowY: "auto",
              }}
            >
              {yearOptions.map((y) => {
                const selected = y === pickerYear;
                // Purely cosmetic marker for the real current year,
                // independent of whichever year is currently being browsed
                // — same idea as a day-grid outlining "today" even when
                // it isn't the selected day.
                const isThisYear = y === dayjs().year();
                return (
                  <ButtonBase
                    key={y}
                    ref={selected ? selectedYearRef : undefined}
                    onClick={() => setPickerYear(y)}
                    sx={{
                      py: 0.75,
                      borderRadius: "16px",
                      fontSize: 14,
                      ...(isThisYear &&
                        !selected && {
                          outline: `1px solid ${colors.Brand[600].$value}`,
                          outlineOffset: "-1px",
                        }),
                      ...(selected && {
                        backgroundColor: colors.Brand[600].$value,
                        color: "#fff",
                        fontWeight: 600,
                      }),
                    }}
                  >
                    {y}
                  </ButtonBase>
                );
              })}
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${YEAR_GRID_COLUMNS}, 1fr)`,
                gap: 0.75,
                // Fixed (not max) height: always reserves this much room,
                // and scrolls internally rather than growing — growing
                // would push the panel past the calendar's fixed-height,
                // overflow-hidden root and get silently clipped.
                height: 100,
                overflowY: "auto",
                pr: 0.25,
              }}
            >
              {MONTH_INDICES.map((m) => {
                const candidate = dayjs(
                  `${pickerYear}-${String(m + 1).padStart(2, "0")}-01`,
                );
                const selected = candidate.isSame(month, "month");
                const isCurrentMonth = candidate.isSame(dayjs(), "month");
                const monthDisabled =
                  monthKey(candidate) < monthKey(dayjs(minDate)) ||
                  monthKey(candidate) > monthKey(dayjs(maxDate));
                return (
                  <ButtonBase
                    key={m}
                    disabled={monthDisabled}
                    onClick={() => handleSelectMonth(candidate)}
                    sx={{
                      py: 0.75,
                      borderRadius: "16px",
                      fontSize: 14,
                      ...(isCurrentMonth &&
                        !selected && {
                          outline: `1px solid ${colors.Brand[600].$value}`,
                          outlineOffset: "-1px",
                        }),
                      ...(selected && {
                        backgroundColor: colors.Brand[600].$value,
                        color: "#fff",
                        fontWeight: 600,
                      }),
                    }}
                  >
                    {candidate.format("MMM")}
                  </ButtonBase>
                );
              })}
            </Box>
          </Paper>
        </ClickAwayListener>
      )}
      <Box>
        <IconButton
          size="small"
          disabled={isPreviousDisabled}
          onClick={() => goToMonth(month.subtract(1, "month"), "right")}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          disabled={isNextDisabled}
          onClick={() => goToMonth(month.add(1, "month"), "left")}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};
